import React from 'react';
import { Container } from '@/components/elements/Container';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { EmailPopup } from '@/components/landing/landingTemplate/EmailPopup';
import { FAQ } from '@/components/landing/landingTemplate/FAQ';
import { useTranslationContext } from '@/context/translation';
import {
	useEmailFunnelFlow,
	useMounted,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
	useUserCountryCode,
} from '@/hooks';
import {
	AmplitudeService,
	CookiesStorageService,
	FacebookService,
	GenerateLandingLinkService,
	GoogleService,
} from '@/services';
import { getLandingUserEmail, getMonetizationTest } from '@/helpers';
import {
	ACCOUNT_SETTINGS_LINK,
	AMPLITUDE_EVENT_CONTEXT,
	AMPLITUDE_EVENT_PROPERTIES,
	AMPLITUDE_EVENTS,
	CONSTANTS,
	COOKIES_KEYS,
	FACEBOOK_EVENTS,
	GOOGLE_EVENTS,
	LANDING_COOKIE_STORAGE,
	LOCAL_STORAGE_KEYS,
	MONETIZATION_TEST,
	SUPPORT_MAIL_LINK,
} from '@/constants';
import LockIcon from '@/public/icons/lock.svg';
import { PricingCta } from './components/PricingCta';
import { PricingFeatures } from './components/PricingFeatures';
import { PricingOffer } from './components/PricingOffer';
import { PricingTestimonials } from './components/PricingTestimonials';
import {
	getDefaultPlanId,
	PRICING_FAQ,
	PRICING_PLAN_NON_TIER_1_NO_TRIAL,
	PRICING_PLANS_NON_TIER_1,
	type PricingPlan,
} from './data';
import {
	getDefaultPricingPlans,
	getMultiCurrencyPricingPlans,
} from './multiCurrencyPrices';
import styles from './Pricing.module.scss';

const NAMESPACE = 'pricing';

const Pricing: React.FC = () => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		NAMESPACE,
		locale,
		translations?.[NAMESPACE] as Record<string, unknown> | undefined,
	);
	const { t: tCommon } = useTranslationWithLocale('common', locale);

	// Same flow as the email-gated landings: the CTA creates an anonymous user
	// and asks for the email, then sends the visitor to the web app's checkout,
	// which picks the plan up from the cookie written below
	const checkoutUrl = GenerateLandingLinkService.webAppPayment();
	const { open: openEmailPopup, popupProps } = useEmailFunnelFlow({
		funnelLinkService: checkoutUrl,
		t,
		tCommon,
	});

	// Geo and the m_test cookie only exist on the client, so the default plan set
	// is what gets server-rendered (and cached) and the test swaps it after mount.
	// The country is looked up asynchronously, so this re-renders once it lands.
	const isMounted = useMounted();
	const { countryCode, isGeoResolved } = useUserCountryCode();
	const isNonTier1 =
		isMounted && getMonetizationTest() === MONETIZATION_TEST.NON_TIER_1;
	const offerVariant = isNonTier1 ? 'nonTier1' : 'default';

	// Where Solid prices the multi-currency products, the non_tier_1 card sells
	// those SKUs and quotes the local amounts Solid is configured to charge —
	// the checkout picks the product up from the cookie written below, so the
	// quote and the charge stay the same thing. Everywhere else: the USD set.
	const isTrialUnavailable =
		isMounted &&
		CookiesStorageService.get<boolean | null>(
			COOKIES_KEYS.TRIAL_AVAILABLE,
			null,
		) === false;
	const nonTier1Plans = React.useMemo(() => {
		const multiCurrencyPlans = getMultiCurrencyPricingPlans(
			countryCode,
			!isTrialUnavailable,
		);
		if (multiCurrencyPlans) return multiCurrencyPlans;

		if (!isTrialUnavailable) return PRICING_PLANS_NON_TIER_1;

		return PRICING_PLANS_NON_TIER_1.map(plan =>
			plan.id === 'trial-limited' ? PRICING_PLAN_NON_TIER_1_NO_TRIAL : plan,
		);
	}, [countryCode, isTrialUnavailable]);
	const defaultPlans = React.useMemo(
		() => getDefaultPricingPlans(countryCode),
		[countryCode],
	);
	const plans = isNonTier1 ? nonTier1Plans : defaultPlans;

	const isAwaitingPlans = !isMounted || !isGeoResolved;

	// A visitor who already gave their email through the popup goes straight to
	// the checkout — there is nothing left to patch
	const hasPatchedEmail = isMounted && !!getLandingUserEmail();

	// Kept here so the fixed phone CTA and the card share one selection
	const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(
		null,
	);
	// Falls back to the recommended plan, also when the plan set has just swapped
	const activePlanId =
		selectedPlanId && plans.some(plan => plan.id === selectedPlanId)
			? selectedPlanId
			: getDefaultPlanId(plans);
	const activePlan = plans.find(plan => plan.id === activePlanId);
	const isTrialPlan = (plan?: PricingPlan) => !!plan?.billing.trialDurationKey;
	const activeProductName = activePlan?.productName;

	// The checkout reads the product from this cookie (same domain in production),
	// so it is written for the preselected plan too, not only on a click
	React.useEffect(() => {
		if (!activeProductName || isAwaitingPlans) return;

		CookiesStorageService.set(
			LANDING_COOKIE_STORAGE.PLAN_FROM_LANDING,
			activeProductName,
		);
	}, [activeProductName, isAwaitingPlans]);

	// Analytics — the same events the web app's paywall sends, under this page's
	// own `context`. `is trial` is derived from the product name the way the
	// paywall derives it, so both ends of the funnel agree on the value.
	const eventProperties = React.useMemo(
		() => ({
			[AMPLITUDE_EVENT_PROPERTIES.CONTEXT]:
				AMPLITUDE_EVENT_CONTEXT.PRICING_LANDING,
			[AMPLITUDE_EVENT_PROPERTIES.SUBSCRIPTION]: activeProductName
				? [activeProductName]
				: [],
			[AMPLITUDE_EVENT_PROPERTIES.IS_TRIAL]:
				!!activeProductName && !activeProductName.includes('no_trial'),
		}),
		[activeProductName],
	);

	useSendToAmplitudeScrolledPercent(
		[25, 50, 100],
		LOCAL_STORAGE_KEYS.PRICING_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.SUBSCRIPTION_PAGE_SCROLLED,
		eventProperties,
	);

	// Sent once the plan set is resolved, so `subscription` reports the plan the
	// visitor actually sees rather than the server-rendered default
	const shownEventSent = React.useRef(false);
	React.useEffect(() => {
		if (!isMounted || !isGeoResolved || shownEventSent.current) return;
		shownEventSent.current = true;

		AmplitudeService.logEvent(AMPLITUDE_EVENTS.SUBSCRIPTION_SHOWN, {
			...eventProperties,
		});
		GoogleService.logEvent(GOOGLE_EVENTS.SUBSCRIPTION_SHOWN);
	}, [isMounted, isGeoResolved, eventProperties]);

	const logContinueEvents = () => {
		FacebookService.logEvent(FACEBOOK_EVENTS.ADD_TO_CART);
		GoogleService.logEvent(GOOGLE_EVENTS.SUBSCRIPTION_CONTINUE, {
			...eventProperties,
		});
	};

	const handleCtaClick = () => {
		logContinueEvents();

		if (hasPatchedEmail) {
			GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
			window.location.href = checkoutUrl;
			return;
		}

		openEmailPopup();
	};

	const faqLinks = {
		'{tools}': (
			<LinkWithQuery href='/tools' className={styles['pricing__faq-link']}>
				{t('faqData_subtitle1Tools')}
			</LinkWithQuery>
		),
		'{settings}': (
			<a href={ACCOUNT_SETTINGS_LINK} className={styles['pricing__faq-link']}>
				{t('faqData_subtitle4Settings')}
			</a>
		),
		'{email}': (
			<a href={SUPPORT_MAIL_LINK} className={styles['pricing__faq-link']}>
				{CONSTANTS.SUPPORT_MAIL_ADDRESS}
			</a>
		),
	};

	return (
		<div className={styles['pricing']}>
			<Container className={styles['pricing__container']}>
				<div className={styles['pricing__heading']}>
					<h1 className={styles['pricing__title']}>{t('hero_title')}</h1>
					<p className={styles['pricing__subtitle']}>{t('hero_subtitle')}</p>
				</div>

				<div className={styles['pricing__content']}>
					<PricingFeatures
						t={t}
						locale={locale}
						isTrialCredits={!isAwaitingPlans && isTrialPlan(activePlan)}
						hasTrialCredits={!isAwaitingPlans && plans.some(isTrialPlan)}
					/>

					<div className={styles['pricing__offer']}>
						<PricingOffer
							t={t}
							variant={offerVariant}
							plans={plans}
							activePlanId={activePlanId}
							isLoading={isAwaitingPlans}
							onSelectPlan={setSelectedPlanId}
							onSubmit={handleCtaClick}
						/>
					</div>
				</div>

				<PricingTestimonials t={t} />
			</Container>

			<FAQ
				data={PRICING_FAQ}
				t={t}
				links={faqLinks}
				onClick={logContinueEvents}
				// The FAQ button runs the same email flow through its own popup, and
				// skips it once the visitor has been patched
				useEmailFlow={!hasPatchedEmail}
				funnelLinkService={checkoutUrl}
				emailSubmitText={t('emailPopup_button')}
				emailTermsIcon={<LockIcon aria-hidden='true' focusable='false' />}
				// The cancellation question is already in this page's own list
				withSubscriptionItem={false}
			/>

			{/* Phones keep the CTA within reach; it settles above the footer */}
			<div className={styles['pricing__sticky-cta']}>
				<PricingCta t={t} disabled={isAwaitingPlans} onClick={handleCtaClick} />
			</div>

			<EmailPopup
				{...popupProps}
				submitText={t('emailPopup_button')}
				termsIcon={<LockIcon aria-hidden='true' focusable='false' />}
			/>
		</div>
	);
};

export default Pricing;
